import { useEffect, useState, useCallback, useMemo } from "react";
import { api, apiFile, formatCurrency, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import HelpTip from "../components/Help/HelpTip";
import EmptyState from "../components/Help/EmptyState";
import { RentRollSkeleton, DataErrorState } from "../components/Skeleton/SkeletonComponents";
import { FileDown, AlertTriangle, Users, ArrowUpDown, X } from "lucide-react";

import { toast } from "sonner";

function getOrdinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

export default function RentRoll() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("unit");
  const [selectedRow, setSelectedRow] = useState(null);
  const { can } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const startTime = Date.now();
    try {
      const r = await api.get("/rent-roll");
      setRows(r.data || []);
    } catch (err) {
      console.error("Failed to load rent roll:", err);
      setLoadError(err.response?.data?.detail || "Failed to load rent roll");
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 300 - elapsed);
      setTimeout(() => {
        setLoading(false);
      }, remaining);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generateReceipt = async (row) => {
    if (!row.tenant_id) {
      toast.error("No tenant linked to this unit");
      return;
    }
    setBusy(row.id);
    try {
      const fd = new FormData();
      fd.append("tenant_id", row.tenant_id);
      fd.append("amount", row.monthly_rent);
      const period = new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });
      fd.append("period", period);
      fd.append("payment_date", new Date().toISOString().slice(0, 10));
      const r = await apiFile.post("/pdf/rent-receipt", fd);
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rent-receipt-${row.tenant_name?.replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Receipt PDF downloaded");
    } catch (e) {
      toast.error("Failed to generate PDF");
    } finally {
      setBusy(null);
    }
  };

  // Filter and sort controls (Move 4: Sorting & filtering move to controls)
  const filteredAndSortedRows = useMemo(() => {
    let result = [...rows];

    if (statusFilter === "Current") {
      result = result.filter((r) => r.payment_status === "Paid" || r.payment_status === "Current");
    } else if (statusFilter === "Overdue") {
      result = result.filter((r) => r.payment_status === "Overdue" || r.payment_status === "No Payments");
    }

    result.sort((a, b) => {
      if (sortBy === "rent-desc") return (b.monthly_rent || 0) - (a.monthly_rent || 0);
      if (sortBy === "rent-asc") return (a.monthly_rent || 0) - (b.monthly_rent || 0);
      if (sortBy === "due-day") return (a.rent_due_day || 1) - (b.rent_due_day || 1);
      return (a.name || "").localeCompare(b.name || "");
    });

    return result;
  }, [rows, statusFilter, sortBy]);

  return (
    <Layout
      title="Rent Roll"
      subtitle={loading ? "Loading rent roll..." : `${rows.length} long-term lease units`}
    >
      {loading ? (
        <RentRollSkeleton />
      ) : loadError ? (
        <DataErrorState message={loadError} onRetry={load} />
      ) : (
        <div className="space-y-4">
          {/* Move 4: Sorting & Filtering Control Strip */}
          <div className="flex flex-wrap items-center justify-between gap-3 p-1">
            <div className="segmented">
              {["All", "Current", "Overdue"].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setStatusFilter(f)}
                  className={`segmented-btn ${statusFilter === f ? "segmented-btn-active" : ""}`}
                >
                  {f}
                  <span className="ml-1 text-[10px] opacity-75">
                    (
                    {f === "All"
                      ? rows.length
                      : f === "Current"
                      ? rows.filter((r) => r.payment_status === "Paid" || r.payment_status === "Current").length
                      : rows.filter((r) => r.payment_status === "Overdue" || r.payment_status === "No Payments").length}
                    )
                  </span>
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs text-[var(--muted)] flex items-center gap-1 font-medium">
                <ArrowUpDown size={13} /> Sort:
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="lapen-input text-xs py-1.5 px-2 w-auto bg-[var(--surface)]"
              >
                <option value="unit">Unit Name (A–Z)</option>
                <option value="rent-desc">Highest Rent</option>
                <option value="rent-asc">Lowest Rent</option>
                <option value="due-day">Due Day (1st–31st)</option>
              </select>
            </div>
          </div>

          {/* Move 6: Container Breakpoint (700px) */}
          <div className="table-responsive-container card overflow-hidden" data-testid="rent-roll-list">
            {filteredAndSortedRows.length === 0 ? (
              <div className="p-8 text-center">
                <EmptyState
                  icon={Users}
                  title="No Matching Units"
                  description={
                    rows.length === 0
                      ? "A rent roll tracks lease terms, monthly rent rates, payment status, and generates PDF rent receipts."
                      : `No rental units match the "${statusFilter}" status filter.`
                  }
                  helpTopic="rent-roll"
                  helpText="About Rent Roll & Overdue Tracking"
                  testId="rent-roll-empty-state"
                />
              </div>
            ) : (
              <>
                {/* ========================================================
                    DESKTOP TABLE VIEW (Visible when container >= 700px)
                   ======================================================== */}
                <div className="table-desktop-view overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-[var(--border)] bg-[var(--surface-hover)] text-[var(--muted)] font-bold uppercase tracking-wider text-[10.5px]">
                        <th className="p-3.5 pl-4">Unit & Address</th>
                        <th className="p-3.5">Tenant</th>
                        <th className="p-3.5 text-right">Monthly Rent</th>
                        <th className="p-3.5 text-center">Due Day</th>
                        <th className="p-3.5">Last Paid</th>
                        <th className="p-3.5 text-center">Status</th>
                        <th className="p-3.5 pr-4 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border)]">
                      {filteredAndSortedRows.map((r) => {
                        const overdue = r.payment_status === "Overdue" || r.payment_status === "No Payments";
                        return (
                          <tr
                            key={r.id}
                            onClick={() => setSelectedRow(r)}
                            className="hover:bg-[var(--surface-hover)]/70 transition-colors cursor-pointer group"
                            data-testid={`rent-row-${r.id}`}
                          >
                            <td className="p-3.5 pl-4">
                              <div className="font-bold text-sm text-[var(--text)] group-hover:text-teal-400 transition-colors">
                                {r.name}
                              </div>
                              <div className="text-[11px] text-[var(--muted)]">{r.address}</div>
                            </td>
                            <td className="p-3.5 font-medium text-[var(--text)]">
                              {r.tenant_name || <span className="text-[var(--muted)] italic">Vacant</span>}
                            </td>
                            <td className="p-3.5 text-right font-bold text-sm tabular-nums font-mono text-[var(--text)]">
                              {formatCurrency(r.monthly_rent)}
                            </td>
                            <td className="p-3.5 text-center text-[var(--muted)] tabular-nums font-medium">
                              Due {r.rent_due_day || 1}{getOrdinal(r.rent_due_day || 1)}
                            </td>
                            <td className="p-3.5 text-[var(--muted)] whitespace-nowrap">
                              {r.last_payment_date ? formatDate(r.last_payment_date) : "—"}
                            </td>
                            <td className="p-3.5 text-center">
                              <span className={`tag ${overdue ? "tag-danger" : "tag-success"}`}>
                                {overdue && <AlertTriangle size={11} className="mr-1 inline" />}
                                {r.payment_status}
                              </span>
                            </td>
                            <td className="p-3.5 pr-4 text-center" onClick={(e) => e.stopPropagation()}>
                              {can.export && (
                                <button
                                  onClick={() => generateReceipt(r)}
                                  disabled={busy === r.id || !r.tenant_id}
                                  data-testid={`receipt-btn-${r.id}`}
                                  className="btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1.5 disabled:opacity-50"
                                >
                                  <FileDown size={13} />
                                  <span>{busy === r.id ? "PDF…" : "Receipt"}</span>
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ========================================================
                    MOBILE CARDS VIEW (Visible when container < 700px)
                    Restructured according to "The Six Moves":
                    - Ranked by use: Identity, Value, State
                    - 2 lines per row, thumb-sized
                    - Line 1: Name left, Amount top-right (one slot, tabular figures)
                    - Line 2: Status & Tenant left, Labeled Due Date right
                    - Tap row: Bottom sheet slides up
                   ======================================================== */}
                <div className="table-cards-view divide-y divide-[var(--border)]">
                  {filteredAndSortedRows.map((r) => {
                    const overdue = r.payment_status === "Overdue" || r.payment_status === "No Payments";
                    const dueDayNum = r.rent_due_day || 1;

                    return (
                      <div
                        key={r.id}
                        onClick={() => setSelectedRow(r)}
                        className="thumb-row hover:bg-[var(--surface-hover)] active:bg-[var(--surface-hover)] transition-colors group cursor-pointer select-none"
                        data-testid={`rent-card-${r.id}`}
                        role="button"
                        tabIndex={0}
                      >
                        {/* Line 1: Identity (Left) & Value (Top Right Slot) */}
                        <div className="flex items-baseline justify-between gap-3 min-w-0">
                          <span className="font-bold text-[15px] text-[var(--text)] group-hover:text-teal-400 transition-colors truncate min-w-0">
                            {r.name}
                          </span>
                          {/* Move 3: ONE SLOT FOR THE AMOUNT - Top right, every card, tabular figures */}
                          <span className="card-amount-slot text-[16px] text-teal-400">
                            {formatCurrency(r.monthly_rent)}
                          </span>
                        </div>

                        {/* Line 2: State & Tenant (Left) & Labeled Date (Right) */}
                        <div className="flex items-center justify-between gap-3 text-xs mt-1.5 min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0 truncate">
                            <span className="text-[var(--muted)] truncate font-medium">
                              {r.tenant_name || "Vacant"}
                            </span>
                            <span className="text-[var(--border)]">·</span>
                            <span className={`tag py-0 px-1.5 text-[10px] shrink-0 ${overdue ? "tag-danger" : "tag-success"}`}>
                              {overdue && <AlertTriangle size={10} className="mr-0.5 inline" />}
                              {r.payment_status}
                            </span>
                          </div>

                          {/* Move 4: Label the ambiguous, omit the obvious ("Due 1st" instead of bare "1") */}
                          <div className="text-[var(--muted)] font-medium shrink-0 whitespace-nowrap text-[11px]">
                            {overdue
                              ? `Due ${dueDayNum}${getOrdinal(dueDayNum)}`
                              : r.last_payment_date
                              ? `Paid ${formatDate(r.last_payment_date)}`
                              : `Due ${dueDayNum}${getOrdinal(dueDayNum)}`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ========================================================
          Move 5: PROGRESSIVE DISCLOSURE VIA BOTTOM SHEET
          "Hidden isn't deleted. Tap the row. Full record, bottom sheet,
          never a new page for a glance."
         ======================================================== */}
      {selectedRow && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-150"
          onClick={() => setSelectedRow(null)}
          data-testid="rent-roll-detail-modal"
        >
          <div
            className="card w-full max-w-lg p-0 overflow-hidden shadow-2xl rounded-t-3xl sm:rounded-3xl max-h-[90vh] flex flex-col my-0 sm:my-auto animate-in slide-in-from-bottom-4 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile Drawer Pull Indicator */}
            <div className="sm:hidden pt-3 pb-1 flex justify-center">
              <div className="w-12 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700" />
            </div>

            {/* Header */}
            <div className="p-5 border-b border-[var(--border)] flex items-start justify-between bg-[var(--surface-hover)]">
              <div className="min-w-0 pr-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-teal-400">
                  Lease Record Glance
                </span>
                <h3 className="text-xl font-extrabold text-[var(--text)] mt-0.5 truncate">
                  {selectedRow.name}
                </h3>
                <p className="text-xs text-[var(--muted)] mt-0.5 truncate">
                  {selectedRow.address}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRow(null)}
                className="p-1.5 rounded-xl text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface)] transition-colors shrink-0"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto">
              {/* Large Rent Hero */}
              <div className="p-4 rounded-2xl bg-[var(--surface)] border border-[var(--border)] flex items-center justify-between">
                <div>
                  <div className="text-xs text-[var(--muted)] font-medium">Monthly Contract Rent</div>
                  <div className="text-2xl font-black text-teal-400 tabular-nums font-mono mt-0.5">
                    {formatCurrency(selectedRow.monthly_rent)}
                    <span className="text-xs font-normal text-[var(--muted)] ml-1">/ month</span>
                  </div>
                </div>
                <span
                  className={`tag text-xs px-3 py-1 font-semibold ${
                    selectedRow.payment_status === "Overdue" || selectedRow.payment_status === "No Payments"
                      ? "tag-danger"
                      : "tag-success"
                  }`}
                >
                  {selectedRow.payment_status}
                </span>
              </div>

              {/* Grid of Key Fields */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--border)]">
                  <span className="text-[var(--muted)] block text-[11px]">Tenant Name</span>
                  <strong className="text-sm text-[var(--text)] block mt-0.5 truncate">
                    {selectedRow.tenant_name || "Vacant Unit"}
                  </strong>
                </div>

                <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--border)]">
                  <span className="text-[var(--muted)] block text-[11px]">Payment Due Day</span>
                  <strong className="text-sm text-[var(--text)] block mt-0.5">
                    {selectedRow.rent_due_day || 1}
                    {getOrdinal(selectedRow.rent_due_day || 1)} of every month
                  </strong>
                </div>

                <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--border)] col-span-2">
                  <span className="text-[var(--muted)] block text-[11px]">Last Payment Received Date</span>
                  <strong className="text-sm text-[var(--text)] block mt-0.5">
                    {selectedRow.last_payment_date
                      ? formatDate(selectedRow.last_payment_date)
                      : "No recorded payments yet this cycle"}
                  </strong>
                </div>
              </div>
            </div>

            {/* Footer with Actions */}
            <div className="p-4 border-t border-[var(--border)] bg-[var(--surface-hover)] flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setSelectedRow(null)}
                className="btn-secondary text-xs py-2 px-4"
              >
                Close
              </button>
              {can.export && (
                <button
                  type="button"
                  onClick={() => generateReceipt(selectedRow)}
                  disabled={busy === selectedRow.id || !selectedRow.tenant_id}
                  data-testid={`modal-receipt-btn-${selectedRow.id}`}
                  className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5 disabled:opacity-50"
                >
                  <FileDown size={14} />
                  <span>{busy === selectedRow.id ? "Generating PDF…" : "Receipt PDF"}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
